import React, { useState } from 'react';
import { suggestBacktestFixes, improveBacktest } from '../api';
import { track, Events } from '../analytics';
import Spinner from './Spinner';

// Shown from the empty-result banner. Ways forward without dumping the user into
// the builder: (1) get concrete AI suggestions for why it didn't trade, (2) let AI
// implement them and re-test in one click, or (3) jump into Quick Edit to tweak
// params and re-run in place.
export default function FixEmptyBacktestModal({ backtest, onEditInQuickEdit, onRetested, onClose, onRequiresSignup }) {
  const [loading, setLoading] = useState(false);
  const [implementing, setImplementing] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState(null);

  const handleSuggest = async () => {
    setError('');
    setLoading(true);
    track(Events.EMPTY_BACKTEST_SUGGEST_CLICKED, { backtest_id: backtest.id });
    try {
      const { data } = await suggestBacktestFixes(backtest.id);
      setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || err?.response?.data?.requires_signup) {
        onRequiresSignup && onRequiresSignup('Sign up to use AI features.');
        onClose && onClose();
        return;
      }
      setError(err?.response?.data?.detail || 'Could not get suggestions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImplement = async () => {
    setError('');
    setImplementing(true);
    track(Events.EMPTY_BACKTEST_IMPLEMENT_CLICKED, { backtest_id: backtest.id });
    try {
      const { data } = await improveBacktest(backtest.id, { suggestions: suggestions || [], run: true });
      if (data?.backtest) {
        onRetested && onRetested(data.backtest);
        onClose && onClose();
      } else if (data?.backtest_error) {
        setError(`Re-test failed: ${data.backtest_error}`);
      } else {
        setError('The AI could not produce a working strategy. Try Quick Edit instead.');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || err?.response?.data?.requires_signup) {
        onRequiresSignup && onRequiresSignup('Sign up to use AI features.');
        onClose && onClose();
        return;
      }
      setError(err?.response?.data?.detail || 'Could not implement the fix. Please try again.');
    } finally {
      setImplementing(false);
    }
  };

  const handleEdit = () => {
    track(Events.EMPTY_BACKTEST_QUICKEDIT_CLICKED, { backtest_id: backtest.id });
    onEditInQuickEdit && onEditInQuickEdit();
    onClose && onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
        padding: 28, maxWidth: 560, width: '100%', position: 'relative', maxHeight: '85vh', overflowY: 'auto',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
          color: 'var(--muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1,
        }}>✕</button>

        <h3 style={{ margin: '0 0 6px', color: 'var(--text)', fontSize: 18 }}>
          Get this strategy trading
        </h3>
        <p style={{ color: 'var(--muted)', margin: '0 0 20px', fontSize: 13, lineHeight: 1.6 }}>
          It didn’t place any trades — the entry conditions never triggered over this period.
          Get AI suggestions for what to change, then tweak it in Quick Edit and re-run.
        </p>

        {/* AI suggestions */}
        {suggestions === null ? (
          <button onClick={handleSuggest} disabled={loading} style={{ ...primaryBtn, width: '100%', marginBottom: 12 }}>
            {loading ? <Spinner size={16} /> : '💡 Ask AI for suggestions'}
          </button>
        ) : (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
            padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
              💡 Suggestions to make it trade
            </div>
            {suggestions.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                No specific suggestions came back — try Quick Edit to loosen the entry conditions.
              </div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text)', fontSize: 13, lineHeight: 1.6 }}>
                {suggestions.map((s, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>{s}</li>
                ))}
              </ul>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12, flexWrap: 'wrap' }}>
              <button onClick={handleImplement} disabled={implementing || loading} style={{ ...primaryBtn, minWidth: 190 }}>
                {implementing ? <Spinner size={16} /> : '✅ Implement & re-test'}
              </button>
              <button onClick={handleSuggest} disabled={loading || implementing} style={linkBtn}>
                {loading ? 'Regenerating…' : '↻ Regenerate'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(248,81,73,0.12)', border: '1px solid rgba(248,81,73,0.4)',
            borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#f85149', fontSize: 13,
          }}>{error}</div>
        )}

        {/* Manual alternative — its own line, clearly separated from the AI path. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          margin: '20px 0 16px', color: 'var(--muted)', fontSize: 12,
        }}>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          or tweak it yourself
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>
        <button
          onClick={handleEdit}
          style={{ ...secondaryBtn, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          ✏️ Edit in Quick Edit
        </button>

        <button onClick={onClose} style={{ ...textLink, display: 'block', margin: '18px auto 0' }}>
          Close
        </button>
      </div>
    </div>
  );
}

const primaryBtn = {
  background: 'var(--teal)', color: 'var(--bg)', border: 'none',
  padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 700,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
};

const secondaryBtn = {
  background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)',
  padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};

const linkBtn = {
  background: 'none', border: 'none', color: 'var(--teal)', fontSize: 12,
  cursor: 'pointer', padding: 0, fontWeight: 600,
};

const textLink = {
  background: 'none', border: 'none', color: 'var(--muted)', fontSize: 13,
  cursor: 'pointer', padding: 4, fontWeight: 500,
};
